import React, { useState, useEffect } from 'react';
import { get } from '../utils/api';

const ACTION_COLORS = {
  create: { bg:'#E8F5EB', color:'#2E6B3A' },
  update: { bg:'#EDF4FF', color:'#1E4FAD' },
  delete: { bg:'#FDEAEA', color:'#8F2E24' },
  approve: { bg:'#E8F5EB', color:'#2E6B3A' },
  reject: { bg:'#FDEAEA', color:'#8F2E24' },
  reverse: { bg:'#FFF3DC', color:'#8B5E0A' },
  login: { bg:'#F2EFE7', color:'#556B2F' },
  logout: { bg:'#F2EFE7', color:'#8A8A8A' },
  status_change: { bg:'#EDF4FF', color:'#1E4FAD' },
  approve_adjustment: { bg:'#E8F5EB', color:'#2E6B3A' },
};

const ENTITY_TYPES = [
  'invoice','payment','bill','expense','refund','job_order','payroll_entry',
  'inventory','product','material','customer','supplier','user','purchase_order'
];

export default function AuditLog() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [filters, setFilters] = useState({ entity_type:'', action:'', from:'', to:'' });
  const [search, setSearch] = useState('');

  const load = () => {
    setLoading(true);
    const qs = new URLSearchParams();
    if (filters.entity_type) qs.append('entity_type', filters.entity_type);
    if (filters.action) qs.append('action', filters.action);
    if (filters.from) qs.append('from', filters.from);
    if (filters.to) qs.append('to', filters.to);
    qs.append('limit', '200');
    get('/audit?'+qs.toString()).then(d => setLogs(d.data||[])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filters]);

  const filtered = search
    ? logs.filter(l => 
        l.user_name?.toLowerCase().includes(search.toLowerCase()) ||
        l.entity_type?.toLowerCase().includes(search.toLowerCase()) ||
        l.action?.toLowerCase().includes(search.toLowerCase())
      )
    : logs;

  const getActionStyle = (action) => ACTION_COLORS[action] || { bg:'#F0F0F0', color:'#8A8A8A' };

  const formatData = (data) => {
    if (!data) return null;
    try {
      const parsed = typeof data === 'string' ? JSON.parse(data) : data;
      return JSON.stringify(parsed, null, 2);
    } catch { return String(data); }
  };

  return (
    <div>
      <div className="flex-between mb-lg">
        <div>
          <h2 className="page-title">Audit Log</h2>
          <p className="text-muted text-sm" style={{marginTop:4}}>Complete record of all system changes</p>
        </div>
        <button className="btn btn-outline btn-sm" onClick={load}>↻ Refresh</button>
      </div>

      {/* Filters */}
      <div style={{background:'#F2EFE7',borderRadius:8,padding:14,marginBottom:16,display:'flex',gap:10,flexWrap:'wrap',alignItems:'flex-end'}}>
        <div className="form-group" style={{margin:0}}>
          <label className="form-label">Entity Type</label>
          <select className="form-control" style={{fontSize:12}} value={filters.entity_type}
            onChange={e=>setFilters({...filters,entity_type:e.target.value})}>
            <option value="">All Types</option>
            {ENTITY_TYPES.map(t=><option key={t} value={t}>{t.replace('_',' ')}</option>)}
          </select>
        </div>
        <div className="form-group" style={{margin:0}}>
          <label className="form-label">Action</label>
          <select className="form-control" style={{fontSize:12}} value={filters.action}
            onChange={e=>setFilters({...filters,action:e.target.value})}>
            <option value="">All Actions</option>
            {Object.keys(ACTION_COLORS).map(a=><option key={a} value={a}>{a.replace('_',' ')}</option>)}
          </select>
        </div>
        <div className="form-group" style={{margin:0}}>
          <label className="form-label">From</label>
          <input type="date" className="form-control" value={filters.from}
            onChange={e=>setFilters({...filters,from:e.target.value})} />
        </div>
        <div className="form-group" style={{margin:0}}>
          <label className="form-label">To</label>
          <input type="date" className="form-control" value={filters.to}
            onChange={e=>setFilters({...filters,to:e.target.value})} />
        </div>
        <input className="form-control" style={{fontSize:12,maxWidth:180}} placeholder="Search user or type..."
          value={search} onChange={e=>setSearch(e.target.value)} />
        {(filters.entity_type||filters.action||filters.from||filters.to||search) && (
          <button className="btn btn-ghost btn-sm" onClick={()=>{setFilters({entity_type:'',action:'',from:'',to:''});setSearch('');}}>Clear</button>
        )}
      </div>

      <div style={{fontSize:12,color:'#8A8A8A',marginBottom:10}}>{filtered.length} records</div>

      {loading ? <div className="spinner"></div> : filtered.length === 0 ? (
        <div className="empty-state"><h3>No audit records found</h3></div>
      ) : (
        <div style={{background:'#fff',border:'1px solid var(--border-light)',borderRadius:10,overflow:'hidden'}}>
          <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
            <thead>
              <tr style={{background:'#556B2F'}}>
                {['Timestamp','User','Action','Entity Type','Entity ID','Details'].map(h=>(
                  <th key={h} style={{padding:'10px 12px',textAlign:'left',fontSize:9,color:'#F2EFE7',textTransform:'uppercase',letterSpacing:'.06em'}}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((log,i) => {
                const ac = getActionStyle(log.action);
                const isExpanded = expanded === log.id;
                const hasData = log.before_data || log.after_data || log.reason;
                return (
                  <React.Fragment key={log.id}>
                    <tr style={{background:i%2===0?'#fff':'#F9F9F9',cursor:hasData?'pointer':'default'}}
                      onClick={() => hasData && setExpanded(isExpanded ? null : log.id)}>
                      <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',whiteSpace:'nowrap',color:'#8A8A8A'}}>
                        {new Date(log.timestamp).toLocaleString('en-NG')}
                      </td>
                      <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',fontWeight:500}}>
                        {log.user_name||'System'}
                      </td>
                      <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8'}}>
                        <span style={{padding:'3px 10px',borderRadius:20,fontSize:10,fontWeight:700,
                          textTransform:'uppercase',background:ac.bg,color:ac.color}}>
                          {log.action?.replace('_',' ')}
                        </span>
                      </td>
                      <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',textTransform:'capitalize',color:'#555'}}>
                        {log.entity_type?.replace('_',' ')}
                      </td>
                      <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8',color:'#8A8A8A',fontSize:10,fontFamily:'monospace'}}>
                        {log.entity_id?.substring(0,8)}...
                      </td>
                      <td style={{padding:'9px 12px',borderBottom:'1px solid #EDE7D8'}}>
                        {hasData && (
                          <span style={{fontSize:10,color:'#556B2F',fontWeight:600}}>
                            {isExpanded ? '▲ Hide' : '▼ Show'} details
                          </span>
                        )}
                        {log.reason && <span style={{fontSize:10,color:'#8A8A8A',marginLeft:8}}>{log.reason}</span>}
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr style={{background:'#F8F8F8'}}>
                        <td colSpan={6} style={{padding:'12px 16px',borderBottom:'1px solid #EDE7D8'}}>
                          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:16}}>
                            {log.before_data && (
                              <div>
                                <div style={{fontSize:10,fontWeight:600,color:'#8F2E24',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:6}}>Before</div>
                                <pre style={{fontSize:10,background:'#FDEAEA',padding:10,borderRadius:6,overflow:'auto',maxHeight:200,margin:0,color:'#3B2A21'}}>
                                  {formatData(log.before_data)}
                                </pre>
                              </div>
                            )}
                            {log.after_data && (
                              <div>
                                <div style={{fontSize:10,fontWeight:600,color:'#2E6B3A',textTransform:'uppercase',letterSpacing:'.08em',marginBottom:6}}>After</div>
                                <pre style={{fontSize:10,background:'#E8F5EB',padding:10,borderRadius:6,overflow:'auto',maxHeight:200,margin:0,color:'#3B2A21'}}>
                                  {formatData(log.after_data)}
                                </pre>
                              </div>
                            )}
                          </div>
                          {log.ip_address && (
                            <div style={{fontSize:10,color:'#8A8A8A',marginTop:8}}>IP: {log.ip_address}</div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
